import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pie, Line } from "react-chartjs-2";
import "bootstrap/dist/css/bootstrap.min.css";
import {
    Chart,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register required chart elements
Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, ChartDataLabels);

const Dashboard = () => {
    const [vendorData, setVendorData] = useState([]);
    const api_url = process.env.REACT_APP_API_URL;

    useEffect(() => {
        axios
            .get(`${api_url}/api/vendor`)
            .then((response) => {
                setVendorData(response.data);
            })
            .catch((error) => {
                console.error("Error fetching vendor data:", error);
            });
    }, [api_url]);

    // Total customer count
    const totalCustomers = vendorData.length;

    // Count categories
    const categoryCounts = vendorData.reduce((acc, vendor) => {
        acc[vendor.CategoryName] = (acc[vendor.CategoryName] || 0) + 1;
        return acc;
    }, {});

    // Count sources
    const sourceCounts = vendorData.reduce((acc, vendor) => {
        acc[vendor.SourceName] = (acc[vendor.SourceName] || 0) + 1;
        return acc;
    }, {});

    // Count countries
    const countryCounts = vendorData.reduce((acc, vendor) => {
        acc[vendor.Country] = (acc[vendor.Country] || 0) + 1;
        return acc;
    }, {});

    // Count shipped/dispatched over time
    const shippedCounts = vendorData
        .filter((vendor) => vendor.Status === "Shipped / Dispatched")
        .reduce((acc, vendor) => {
            const month = new Date(vendor.Date).toLocaleString("default", { month: "short" });
            acc[month] = (acc[month] || 0) + 1;
            return acc;
        }, {});

    // Pie Chart (SourceName)
    const pieDataSource = {
        labels: Object.keys(sourceCounts),
        datasets: [
            {
                data: Object.values(sourceCounts),
                backgroundColor: ["#28a745", "#ffc107", "#dc3545", "#007bff", "#6c757d"],
            },
        ],
    };

    // Pie Chart (Country)
    const pieDataCountry = {
        labels: Object.keys(countryCounts),
        datasets: [
            {
                data: Object.values(countryCounts),
                backgroundColor: ["#ff6384", "#36a2eb", "#ffcd56", "#4bc0c0", "#9966ff"],
            },
        ],
    };

    // Line Chart (Shipped/Dispatched Growth)
    const lineDataGrowth = {
        labels: Object.keys(shippedCounts),
        datasets: [
            {
                label: "Shipped/Dispatched",
                data: Object.values(shippedCounts),
                borderColor: "#17a2b8",
                backgroundColor: "rgba(23, 162, 184, 0.2)",
                tension: 0.4,
                fill: true,
            },
        ],
    };

    return (
        <div className="container mt-4">
            {/* Total Customers Count */}
            <div className="row mb-4">
                <div className="col-md-12">
                    <div className="card text-center shadow p-4 text-primary">
                        <h2>Total Customers</h2>
                        <h1 className="display-4">{totalCustomers}</h1>
                    </div>
                </div>
            </div>

            {/* Category Counts */}
            <div className="row mb-4">
                {Object.keys(categoryCounts).map((category, index) => (
                    <div key={index} className="col-md-3 mb-3">
                        <div className="card text-center shadow p-3">
                            <h5 className="fw-bold">{category}</h5>
                            <h3 className="text-primary">{categoryCounts[category]}</h3>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="row">
                {/* Source Diversity Pie Chart */}
                <div className="col-md-4 mb-3">
                    <div className="card shadow p-4">
                        <h5 className="text-center mb-3 fw-bold">Source Diversity</h5>
                        <Pie data={pieDataSource} plugins={[ChartDataLabels]} />
                    </div>
                </div>

                {/* Country Diversity Pie Chart */}
                <div className="col-md-4 mb-3">
                    <div className="card shadow p-4">
                        <h5 className="text-center mb-3 fw-bold">Country Diversity</h5>
                        <Pie data={pieDataCountry} plugins={[ChartDataLabels]} />
                    </div>
                </div>

                {/* Growth Line Chart */}
                <div className="col-md-4 mb-6">
                    <div className="card shadow p-4">
                        <h5 className="text-center mb-3 fw-bold">Growth (Shipped/Dispatched)</h5>
                        <Line data={lineDataGrowth} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
